import { useState, useEffect, useCallback } from 'react';
import { getUserHabits, logHabit, unlogHabit, createHabit } from '@/lib/firebase/queries/habits';
import type { Habit } from '@/types/habits';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { addPointsForHabits } from '@/lib/db/collections/points';

export function useHabits() {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [error, setError] = useState<Error | null>(null);

  const fetchHabits = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    try {
      setLoading(true);
      const userHabits = await getUserHabits(currentUser.uid);
      setHabits(userHabits);
    } catch (error) {
      console.error('Error fetching habits:', error);
      toast.error(t('habits.notifications.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, t]);

  useEffect(() => {
    if (currentUser?.uid) {
      fetchHabits();
    }
  }, [currentUser?.uid, fetchHabits]);

  const handleLogHabit = async (habitId: string) => {
    if (!currentUser?.uid) return;

    try {
      await logHabit(habitId, currentUser.uid);
      await fetchHabits(); // Refresh habits after logging
      toast.success(t('habits.notifications.loggedSuccess'));

      // Add points for completing the habit (only once per day)
      await addPointsForHabits(currentUser.uid);

      return true;
    } catch (error: any) {
      console.error('Error logging habit:', error);
      if (error.message === 'already_logged') {
        toast.error(t('habits.notifications.alreadyLogged'));
      } else {
        toast.error(t('habits.notifications.logError'));
      }
      return false;
    }
  };

  const handleUnlogHabit = async (habitId: string) => {
    if (!currentUser?.uid) return;

    try {
      await unlogHabit(habitId, currentUser.uid);
      await fetchHabits(); // Refresh habits after unlogging
      toast.success(t('habits.notifications.unloggedSuccess') || 'Habit unlogged successfully');

      return true;
    } catch (error: any) {
      console.error('Error unlogging habit:', error);
      if (error.message === 'not_logged_today') {
        toast.error(t('habits.notifications.notLoggedToday') || 'Habit was not logged today');
      } else {
        toast.error(t('habits.notifications.unlogError') || 'Failed to unlog habit');
      }
      return false;
    }
  };

  const addHabit = async (habitData: Omit<Habit, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    if (!currentUser) return;

    try {
      const habitId = await createHabit({
        ...habitData,
        userId: currentUser.uid
      });
      
      await fetchHabits(); // Refresh habits after adding
      return habitId;
    } catch (err) {
      setError(err as Error);
      toast.error(t('habits.notifications.createError'));
      return null;
    }
  };

  return {
    habits,
    loading,
    error,
    logHabit: handleLogHabit,
    unlogHabit: handleUnlogHabit,
    refreshHabits: fetchHabits,
    addHabit
  };
}